/**
 * The orders list's view, as the address bar holds it: which status tab,
 * which page, which filters, in what order, which order is open beside the
 * list. A default is not written into the address: `/orders` is every order,
 * the newest first.
 *
 * What the list is searched for is not part of it: the end of a client's
 * phone is personal data, and is kept out of the address
 * (`@/shared/lib/private-search`).
 */
import { dayPlus } from "@/shared/lib/days";
import { instantIn, MARKET_TIME_ZONE } from "@/shared/lib/format";
import { idOf } from "@/shared/lib/search";

import type { OrdersFilter } from "../api/queries";
import {
  CHANNELS,
  ORDER_SORTS,
  PAYMENT_METHODS,
  type Channel,
  type OrderSort,
  type PaymentMethod,
  type VisitStatus,
} from "../api/types";

/**
 * The tabs of the v1 orders page, by where an order's visit stands, in v1's
 * order: everything, searching, escalated, then the visit's way from given to
 * done or cancelled. v1's "accepted" is `assigned` here, its "confirmed" (on
 * the way) `en_route`, its "arrived" and "in progress" are both `on_site`.
 * v2's own two close the row: booked for later, and over without being done.
 *
 * `help` is v1's "escalated", grown: what needs a person, the visits the
 * search could not place and the ones somebody has that do not move.
 */
export const BOARD_TABS = [
  "all",
  "searching",
  "help",
  "assigned",
  "en_route",
  "on_site",
  "completed",
  "cancelled",
  "scheduled",
  "unfulfilled",
] as const;
export type BoardTab = (typeof BOARD_TABS)[number];
export const DEFAULT_TAB: BoardTab = "all";

/** The tabs that are one status of a visit. */
export type StatusTab = Exclude<BoardTab, "all" | "help">;

export interface BoardSearch {
  tab?: BoardTab;
  page?: number;
  /** A service area's id. */
  area?: string;
  /** Where the order was made. */
  channel?: Channel;
  /** How it is paid. */
  payment?: PaymentMethod;
  /** Made on this day or after it, as `YYYY-MM-DD` by the market's clock. */
  from?: string;
  /** Made on this day or before it. */
  to?: string;
  /** The order of the list; left out: the newest first. */
  sort?: OrderSort;
  /** The order open in the side panel. */
  order?: string;
}

/** A day as the address holds it, when it is one. */
function dayOf(value: unknown): string | undefined {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  return Number.isNaN(Date.parse(`${value}T00:00:00Z`)) ? undefined : value;
}

/** The day after a day, both as `YYYY-MM-DD`. */
function dayAfter(day: string): string {
  const next = new Date(`${day}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
}

/** Reads the address. Anything it does not know is dropped, never trusted. */
export function parseBoardSearch(raw: Readonly<Record<string, unknown>>): BoardSearch {
  const search: BoardSearch = {};
  const tab = BOARD_TABS.find((known) => known === raw["tab"]);
  if (tab !== undefined && tab !== DEFAULT_TAB) search.tab = tab;
  const page = Number(raw["page"]);
  if (Number.isInteger(page) && page > 1) search.page = page;
  const area = idOf(raw["area"]);
  if (area !== undefined) search.area = area;
  const channel = CHANNELS.find((known) => known === raw["channel"]);
  if (channel !== undefined) search.channel = channel;
  const payment = PAYMENT_METHODS.find((known) => known === raw["payment"]);
  if (payment !== undefined) search.payment = payment;
  const from = dayOf(raw["from"]);
  if (from !== undefined) search.from = from;
  const to = dayOf(raw["to"]);
  if (to !== undefined) search.to = to;
  const sort = ORDER_SORTS.find((known) => known === raw["sort"]);
  if (sort !== undefined && sort !== DEFAULT_SORT) search.sort = sort;
  const order = idOf(raw["order"]);
  if (order !== undefined) search.order = order;
  return search;
}

/** hg-order's own order of the list: the newest first. */
export const DEFAULT_SORT: OrderSort = "-created_at";

export function tabOf(search: BoardSearch): BoardTab {
  return search.tab ?? DEFAULT_TAB;
}

/**
 * What a tab asks hg-order's list of orders for, with the filters that are
 * set. `help` is not here: it is what needs a person (the queue of
 * escalations and the stuck visits), which takes no filter. `all` asks for
 * every order; a status tab, for the orders with a visit in that status.
 *
 * The days are the market's: from the start of the first to the start of the
 * day after the last.
 *
 * `q` is what the list is searched for (an order's number, the end of a
 * phone, a part of the client's or the specialist's name): it comes from the
 * screen, never from the address.
 */
export function tabFilter(
  tab: Exclude<BoardTab, "help">,
  search: BoardSearch,
  q?: string,
): OrdersFilter {
  const filter: OrdersFilter = {};
  if (tab !== "all") filter.visit_status = tab satisfies VisitStatus;
  if (search.area !== undefined) filter.area_id = search.area;
  if (search.channel !== undefined) filter.channel = search.channel;
  if (search.payment !== undefined) filter.payment_method = search.payment;
  const from =
    search.from === undefined ? null : instantIn(`${search.from}T00:00`, MARKET_TIME_ZONE);
  if (from !== null) filter.created_from = from;
  const to =
    search.to === undefined ? null : instantIn(`${dayAfter(search.to)}T00:00`, MARKET_TIME_ZONE);
  if (to !== null) filter.created_to = to;
  if (search.sort !== undefined) filter.sort = search.sort;
  if (q !== undefined) filter.q = q;
  return filter;
}

/** The view after choosing another tab: its first page, nothing open, the filters kept. */
export function withTab(search: BoardSearch, tab: BoardTab): BoardSearch {
  const next: BoardSearch = {};
  if (tab !== DEFAULT_TAB) next.tab = tab;
  if (search.area !== undefined) next.area = search.area;
  if (search.channel !== undefined) next.channel = search.channel;
  if (search.payment !== undefined) next.payment = search.payment;
  if (search.from !== undefined) next.from = search.from;
  if (search.to !== undefined) next.to = search.to;
  if (search.sort !== undefined) next.sort = search.sort;
  return next;
}

/** The view after a filter changed: the first page of the same tab. */
export function withFilters(
  search: BoardSearch,
  filters: {
    area?: string | undefined;
    channel?: string | undefined;
    payment?: string | undefined;
    from?: string | undefined;
    to?: string | undefined;
    sort?: string | undefined;
  },
): BoardSearch {
  const next: BoardSearch = {};
  if (search.tab !== undefined) next.tab = search.tab;
  if (filters.area !== undefined) next.area = filters.area;
  const channel = CHANNELS.find((known) => known === filters.channel);
  if (channel !== undefined) next.channel = channel;
  const payment = PAYMENT_METHODS.find((known) => known === filters.payment);
  if (payment !== undefined) next.payment = payment;
  const from = dayOf(filters.from);
  if (from !== undefined) next.from = from;
  const to = dayOf(filters.to);
  if (to !== undefined) next.to = to;
  const sort = ORDER_SORTS.find((known) => known === filters.sort);
  if (sort !== undefined && sort !== DEFAULT_SORT) next.sort = sort;
  if (search.order !== undefined) next.order = search.order;
  return next;
}

export function withPage(search: BoardSearch, page: number): BoardSearch {
  const { page: _, ...rest } = search;
  return page > 1 ? { ...rest, page } : rest;
}

export function withOrder(search: BoardSearch, order: string | null): BoardSearch {
  const { order: _, ...rest } = search;
  return order === null ? rest : { ...rest, order };
}

/**
 * v1's quick periods over when an order was made, by the market's days: each
 * sets the two ends of the dates filter. `all` clears them.
 */
export const PERIODS = ["today", "yesterday", "week", "days30", "month", "all"] as const;
export type Period = (typeof PERIODS)[number];

/** The first and last day of a period, as `YYYY-MM-DD`; none for `all`. */
export function periodRange(period: Period, today: string): { from?: string; to?: string } {
  switch (period) {
    case "today":
      return { from: today, to: today };
    case "yesterday": {
      const day = dayPlus(today, -1);
      return { from: day, to: day };
    }
    case "week":
      return { from: dayPlus(today, -6), to: today };
    case "days30":
      return { from: dayPlus(today, -29), to: today };
    case "month":
      return { from: `${today.slice(0, 8)}01`, to: today };
    case "all":
      return {};
  }
}

/** Which quick period the dates filter is, if it is one. */
export function periodOf(search: BoardSearch, today: string): Period | null {
  return (
    PERIODS.find((period) => {
      const range = periodRange(period, today);
      return range.from === search.from && range.to === search.to;
    }) ?? null
  );
}

/** The view after a quick period was chosen: its first page, the rest kept. */
export function withPeriod(search: BoardSearch, period: Period, today: string): BoardSearch {
  const { from: _from, to: _to, page: _page, ...rest } = search;
  return { ...rest, ...periodRange(period, today) };
}
